// Login page — posts to /api/v1/auth/login (BE-003) and then lets the
// FE-A AuthProvider load /api/v1/auth/me before redirecting to the
// dashboard.

import { useMutation } from "@tanstack/react-query";
import { FormEvent, useEffect, useState, type FormEventHandler } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { Button, Input } from "@/design-system";
import {
  parseReturnPathFromState,
  shellFromHostname,
  shellHome,
} from "@/lib/navigation";
import { AuthShell, useAuth } from "@/features/auth";
import { LoginCard } from "@/features/recon/shell";

export interface LoginProps {
  /** Controlled host seam used by Storybook/browser coverage. */
  hostname?: string;
}

export function Login({ hostname }: LoginProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const hostShell = shellFromHostname(hostname ?? window.location.hostname);
  const from = parseReturnPathFromState(
    location.state,
    shellHome(hostShell),
    hostShell,
  );

  useEffect(() => {
    if (auth.status === "authenticated") {
      void navigate(from, { replace: true });
    }
  }, [auth.status, from, navigate]);

  const mutation = useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      auth.login(body),
    onSuccess: () => {
      void navigate(from, { replace: true });
    },
  });

  if (auth.status === "authenticated") {
    return null;
  }

  const onSubmit: FormEventHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutation.mutate({ email, password });
  };

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (
        mutation.error.status === 401
        || /invalid/i.test(mutation.error.message)
      ) {
        return "That email and password combination didn't match. Try again.";
      }
      return mutation.error.message;
    }
    return "Something went wrong signing you in. Try again in a moment.";
  })();

  return (
    <AuthShell layout="centered" testId="login-page" showBrand={false}>
      <LoginCard
        shell={hostShell}
        title="Welcome back"
        subtitle="Sign in with your brokerage credentials."
        embedded
        cardProps={{ className: "shadow-overlay" }}
      >
        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={onSubmit}
          data-testid="login-form"
          noValidate
        >
          <Input
            type="email"
            label="Email"
            autoComplete="email"
            required
            data-testid="login-email"
            placeholder="you@brokerage.com"
            className="min-h-[44px] w-full"
            error={errorMessage ?? undefined}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            type="password"
            label="Password"
            autoComplete="current-password"
            required
            data-testid="login-password"
            className="min-h-[44px] w-full"
            error={errorMessage ? "Check your password and try again." : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {errorMessage && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember-ink"
            >
              {errorMessage}
            </div>
          )}

          <Button
            type="submit"
            loading={mutation.isPending}
            aria-label={mutation.isPending ? "Signing in…" : "Sign in"}
            data-testid="login-submit"
            className="mt-2 min-h-[44px] w-full"
          >
            Sign in
          </Button>
        </form>

        <p className="mt-6 border-t border-border-subtle pt-4 text-xs text-text-tertiary">
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="font-medium text-teal hover:underline"
          >
            Create an account
          </Link>
          . Already on a team and need access? Ask your brokerage administrator
          to send you an invitation.
        </p>
      </LoginCard>
    </AuthShell>
  );
}
